import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { GrantResponse, LabelPermission, UserInfo } from '@/services/api/lake'

export interface AddGrantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  labelName?: string
  users: UserInfo[]
  existingGrants: GrantResponse[]
  onSubmit: (username: string, permission: LabelPermission) => void
  submitting: boolean
}

export function AddGrantDialog({
  open,
  onOpenChange,
  labelName,
  users,
  existingGrants,
  onSubmit,
  submitting,
}: AddGrantDialogProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [permission, setPermission] = useState<LabelPermission>('read')
  const [comboOpen, setComboOpen] = useState(false)

  const reset = () => {
    setUsername('')
    setPermission('read')
    setComboOpen(false)
  }
  const candidates = users.filter(
    u => u.is_active && !existingGrants.some(g => g.username === u.username)
  )

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) reset()
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin.labels.grantAccess')}</DialogTitle>
          <DialogDescription>
            {t('admin.labels.grantAccessDescription', { label: labelName })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{t('admin.labels.user')}</Label>
            <Popover open={comboOpen} onOpenChange={setComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboOpen}
                  className="w-full justify-between"
                >
                  {username ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold uppercase">
                        {username[0]}
                      </span>
                      {username}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{t('admin.labels.selectUser')}</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-(--radix-popover-trigger-width) p-0 z-[300]">
                <Command>
                  <CommandInput placeholder={t('admin.labels.searchUsers')} />
                  <CommandList>
                    <CommandEmpty className="text-muted-foreground text-sm">
                      {t('admin.labels.noUsersFound')}
                    </CommandEmpty>
                    <CommandGroup>
                      {candidates.map(u => (
                        <CommandItem
                          key={u.id}
                          value={u.username}
                          onSelect={() => {
                            setUsername(u.username)
                            setComboOpen(false)
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-2 flex-1 max-md:min-w-0">
                            <span className="w-6 h-6 max-md:shrink-0 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold uppercase text-muted-foreground">
                              {u.username[0]}
                            </span>
                            <div className="flex flex-col max-md:min-w-0">
                              <span className="text-sm max-md:truncate">{u.username}</span>
                              {u.email && (
                                <span className="text-xs text-muted-foreground max-md:truncate">
                                  {u.email}
                                </span>
                              )}
                            </div>
                            {u.is_superuser && (
                              <Badge
                                variant="outline"
                                className="ml-auto max-md:shrink-0 bg-warning-surface text-warning-foreground border-warning-border text-[9px]"
                              >
                                {t('common.superuser')}
                              </Badge>
                            )}
                          </div>
                          {username === u.username && (
                            <Check className="ml-2 h-4 w-4 text-selected-foreground" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>{t('admin.labels.permission')}</Label>
            <Select value={permission} onValueChange={val => setPermission(val as LabelPermission)}>
              <SelectTrigger className="max-md:w-full max-md:data-[size=default]:h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[300]">
                <SelectItem value="read">{t('admin.labels.permRead')}</SelectItem>
                <SelectItem value="read_write">{t('admin.labels.permReadWrite')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => onSubmit(username, permission)}
            disabled={!username.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('admin.labels.grantAccess')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
